"use client";

import { useState } from "react";
import { GOODS_TRANSPORT_VEHICLES } from "@/lib/goodsTransportOptions";
import { compressDriverDocument } from "@/lib/compressDriverDocument";

const passengerVehicles = ["Car", "Bike", "Scooter", "SUV", "Auto"] as const;

type DocumentType = "drivingLicence" | "rc" | "identity" | "insurance";

type ExistingRegistration = {
  category: string;
  vehicleType: string;
  vehicleModel: string;
  vehicleNumber: string;
};

type Props = {
  onSubmitted: () => void;
  existingRegistration?: ExistingRegistration;
};

export default function HomepageDriverRegistrationForm({
  onSubmitted,
  existingRegistration,
}: Props) {
  const [category, setCategory] = useState<"Passenger" | "Goods">(
    existingRegistration?.category === "Goods" ? "Goods" : "Passenger"
  );
  const [vehicleType, setVehicleType] = useState(
    existingRegistration?.vehicleType ?? ""
  );
  const [vehicleModel, setVehicleModel] = useState(
    existingRegistration?.vehicleModel ?? ""
  );
  const [vehicleNumber, setVehicleNumber] = useState(
    existingRegistration?.vehicleNumber ?? ""
  );
  const [identityType, setIdentityType] = useState<"Aadhaar" | "PAN">("Aadhaar");
  const [files, setFiles] = useState<Partial<Record<DocumentType, File>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState("");
  const [previews, setPreviews] = useState<Partial<Record<DocumentType, string>>>({});
  const [preparingCount, setPreparingCount] = useState(0);
  const [selectedNames, setSelectedNames] = useState<Partial<Record<DocumentType, string>>>({});
  const [documentErrors, setDocumentErrors] = useState<Partial<Record<DocumentType, string>>>({});
  const [preparingDocuments, setPreparingDocuments] = useState<Partial<Record<DocumentType, boolean>>>({});

  const vehicleOptions =
    category === "Passenger" ? passengerVehicles : GOODS_TRANSPORT_VEHICLES;

  function documentInput(type: DocumentType, label: string) {
    return (
      <label className="block space-y-1 text-sm font-semibold text-slate-800">
        <span>{label}</span>
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp,application/pdf"
          required
          disabled={submitting}
          onChange={async (event) => {
            const file = event.target.files?.[0];
            setFiles((previous) => ({ ...previous, [type]: undefined }));
            setPreviews((previous) => ({ ...previous, [type]: undefined }));
            setSelectedNames((previous) => ({ ...previous, [type]: file?.name }));
            setDocumentErrors((previous) => ({ ...previous, [type]: undefined }));
            if (!file) return;
            setPreparingDocuments((previous) => ({ ...previous, [type]: true }));

            setPreparingCount((count) => count + 1);
            setError("");

            try {
              const prepared = await compressDriverDocument(file);
              let preview: string | undefined;

              if (prepared.type.startsWith("image/")) {
                preview = await new Promise<string>((resolve, reject) => {
                  const reader = new FileReader();
                  reader.onload = () =>
                    typeof reader.result === "string"
                      ? resolve(reader.result)
                      : reject(new Error("Unable to preview document."));
                  reader.onerror = () =>
                    reject(new Error("Unable to preview document."));
                  reader.readAsDataURL(prepared);
                });
              }

              setFiles((previous) => ({ ...previous, [type]: prepared }));
              setPreviews((previous) => ({ ...previous, [type]: preview }));
            } catch (cause) {
              setError(
                cause instanceof Error ? cause.message : "Document preparation failed."
              );
              setDocumentErrors((previous) => ({
                ...previous,
                [type]: cause instanceof Error ? cause.message : "Document preparation failed.",
              }));
              event.target.value = "";
            } finally {
              setPreparingDocuments((previous) => ({ ...previous, [type]: false }));
              setPreparingCount((count) => count - 1);
            }
          }}
          className="block w-full rounded-xl border border-slate-200 bg-white p-2 text-sm"
        />
        <span className="block text-xs font-normal text-slate-500">
          JPG, PNG, WEBP or PDF · maximum 100 KB
        </span>
          {selectedNames[type] && (
            <span className="block break-all text-xs font-medium text-slate-700">
              Selected: {selectedNames[type]}
            </span>
          )}
          {preparingDocuments[type] && (
            <span className="block text-xs text-amber-700">Processing document...</span>
          )}
          {documentErrors[type] && (
            <span role="alert" className="block text-xs font-semibold text-red-700">
              Not ready: {documentErrors[type]}
            </span>
          )}
          {previews[type] && (
          <div className="rounded-xl border border-slate-200 p-2">
            <img
              src={previews[type]}
              alt={`${label} compressed preview`}
              className="max-h-64 w-full object-contain"
            />
            <p className="mt-1 text-xs text-slate-600">
              Compressed preview — check that all text is readable.
            </p>
          </div>
        )}
        {files[type] && (
          <span className="block text-xs font-semibold text-emerald-700">
            Ready: {files[type]?.name} ({Math.ceil((files[type]?.size ?? 0) / 1024)} KB)
          </span>
        )}
      </label>
    );
  }

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting || preparingCount > 0) return;

    const types: DocumentType[] = [
      "drivingLicence",
      "rc",
      "identity",
      "insurance",
    ];

    if (!vehicleType || types.some((type) => !files[type])) {
      setError("Select a vehicle and upload all four required documents.");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const uploaded: Record<string, string> = {};

      for (const type of types) {
        const file = files[type];
        if (!file) throw new Error("Upload all required documents.");
        setProgress(`Preparing ${type} (maximum 100 KB)...`);

        const preparedFile = file; // Already prepared and checked during selection.

        if (!preparedFile.size || preparedFile.size > 100 * 1024) {
          throw new Error("Each document must be 100 KB or smaller.");
        }

        setProgress(`Uploading ${type} (${Math.ceil(preparedFile.size / 1024)} KB)...`);

        const form = new FormData();
        form.set("documentType", type);
        form.set("file", preparedFile);

        const response = await fetch("/api/driver-registration/documents", {
          method: "POST",
          body: form,
        });

        const result = await response.json();

        if (!response.ok || !result.documentPath) {
          throw new Error(result.message || "Document upload failed.");
        }

        uploaded[`${type}Path`] = result.documentPath;
      }

      setProgress("Submitting for Admin verification...");

      const response = await fetch("/api/driver-registration", {
        method: existingRegistration ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          category,
          vehicleType,
          vehicleModel,
          vehicleNumber,
          identityType,
          ...uploaded,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Registration submission failed.");
      }

      setProgress("");
      onSubmitted();
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Please try again."
      );
      setProgress("");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm text-slate-600">
        {existingRegistration
          ? "Correct your vehicle details and upload all four documents again."
          : "Register your vehicle once. You can go Online after Admin approval."}
      </p>

      <label className="block space-y-1 text-sm font-semibold text-slate-800">
        Vehicle Category
        <select
          value={category}
          disabled={submitting}
          onChange={(event) => {
            setCategory(event.target.value as "Passenger" | "Goods");
            setVehicleType("");
          }}
          className="w-full rounded-xl border border-slate-200 bg-white p-3"
        >
          <option value="Passenger">Passenger</option>
          <option value="Goods">Goods Transport</option>
        </select>
      </label>

      <label className="block space-y-1 text-sm font-semibold text-slate-800">
        Vehicle Type
        <select
          value={vehicleType}
          required
          disabled={submitting}
          onChange={(event) => setVehicleType(event.target.value)}
          className="w-full rounded-xl border border-slate-200 bg-white p-3"
        >
          <option value="">Select vehicle</option>
          {vehicleOptions.map((vehicle) => (
            <option key={vehicle} value={vehicle}>{vehicle}</option>
          ))}
        </select>
      </label>

      <label className="block space-y-1 text-sm font-semibold text-slate-800">
        Vehicle Model / Name
        <input
          value={vehicleModel}
          onChange={(event) => setVehicleModel(event.target.value)}
          maxLength={80}
          required
          disabled={submitting}
          placeholder="Example: Tata Ace / Swift"
          className="w-full rounded-xl border border-slate-200 bg-white p-3"
        />
      </label>

      <label className="block space-y-1 text-sm font-semibold text-slate-800">
        Vehicle Registration Number
        <input
          value={vehicleNumber}
          onChange={(event) => setVehicleNumber(event.target.value)}
          maxLength={30}
          required
          disabled={submitting}
          placeholder="Example: OD02AB1234"
          className="w-full rounded-xl border border-slate-200 bg-white p-3 uppercase"
        />
      </label>

      <div className="space-y-3 rounded-xl border border-slate-200 p-3">
        <p className="font-bold text-slate-900">Required Documents</p>

        {documentInput("drivingLicence", "Driving Licence")}
        {documentInput("rc", "Vehicle RC")}

        <label className="block space-y-1 text-sm font-semibold text-slate-800">
          Identity Document
          <select
            value={identityType}
            disabled={submitting}
            onChange={(event) =>
              setIdentityType(event.target.value as "Aadhaar" | "PAN")
            }
            className="w-full rounded-xl border border-slate-200 bg-white p-3"
          >
            <option value="Aadhaar">Aadhaar Card</option>
            <option value="PAN">PAN Card</option>
          </select>
        </label>

        {documentInput("identity", `${identityType} Document`)}
        {documentInput("insurance", "Vehicle Insurance")}
      </div>

      {error && (
        <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
          {error}
        </p>
      )}

      {progress && (
        <p role="status" className="text-sm text-indigo-700">{progress}</p>
      )}

      <button
        type="submit"
        disabled={submitting || preparingCount > 0}
        className="w-full rounded-xl bg-indigo-700 px-4 py-3 font-bold text-white disabled:opacity-50"
      >
        {submitting
          ? "Submitting..."
          : preparingCount > 0
            ? "Preparing documents..."
            : existingRegistration
              ? "Resubmit for Verification"
              : "Submit for Verification"}
      </button>
    </form>
  );
}
